/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Layout from './components/layout/Layout';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import Home from './pages/Home';
import Quiz from './pages/Quiz';
import Bestiary from './pages/Bestiary';
import Badges from './pages/Badges';
import Duel from './pages/Duel';
import Profile from './pages/Profile';
import Support from './pages/Support';
import AdminPanel from './pages/AdminPanel';
import PlantEnrichmentDashboard from './pages/PlantEnrichmentDashboard';
import PublicProfile from './pages/PublicProfile';
import Sniggle from './pages/Sniggle';
import Friends from './pages/Friends';
import ApprenticeJournal from './pages/ApprenticeJournal';
import ApprenticeWeekDetail from './pages/ApprenticeWeekDetail';
import PflanzeDerWocheList from './pages/PflanzeDerWocheList';
import PflanzeDerWocheEditor from './pages/PflanzeDerWocheEditor';
import HerbariumManager from './pages/HerbariumManager';
import HerbariumEditor from './pages/HerbariumEditor';
import AuthPage from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Career from './pages/Career';
import GuestWelcome from './pages/GuestWelcome';
import Glossary from './pages/Glossary';
import GuestBlockedView from './components/common/GuestBlockedView';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import Changelog from './pages/Changelog';
import WeatherDetail from './pages/WeatherDetail';
import WeatherDayDetail from './pages/WeatherDayDetail';
import BillingAdmin from './pages/BillingAdmin';
import { LoadingSpinner, IconSun, IconMoon } from './components/ui/Icons';
import PlantDetailModal from './components/common/PlantDetailModal';
import PartyHome from './components/party/PartyHome';
import InstantSniggle from './components/instant-sniggle/InstantSniggle';
import PlantScanner from './pages/PlantScanner';
import CoachDashboard from './pages/CoachDashboard';
import CoachAzubiDetail from './pages/CoachAzubiDetail';
import CoachReportInbox from './pages/CoachReportInbox';
import CoachGroups from './pages/CoachGroups';
import QuizBuilder from './pages/QuizBuilder';
import JoinGroup from './pages/JoinGroup';
import { API_URL } from './utils/api';

const AppRoutes = () => {
    const { user, authToken, loading: authLoading, logout, fetchWithAuth } = useContext(AuthContext);

    // Global State
    const [plants, setPlants] = useState([]);
    const [categories, setCategories] = useState([]);
    const [userPlantData, setUserPlantData] = useState({});
    const [userProfile, setUserProfile] = useState(null);
    const [achievements, setAchievements] = useState(null);
    const [dailyStats, setDailyStats] = useState(null);
    const [dailyChallenge, setDailyChallenge] = useState(null);
    const [messages, setMessages] = useState({ received: [], sent: [], unread_count: 0 });
    const [comparePlants, setComparePlants] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState(null);
    const [sponsors, setSponsors] = useState([]);
    const [appVersion, setAppVersion] = useState('v2.0');

    // UI State
    const [theme, setTheme] = useState(() => {
        try { return localStorage.getItem('theme') || 'light'; } catch { return 'light'; }
    });

    const [appLoading, setAppLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    // Redirection after logout
    useEffect(() => {
        const handleLogout = () => {
            navigate('/auth');
        };

        window.addEventListener('auth:logout', handleLogout);
        return () => window.removeEventListener('auth:logout', handleLogout);
    }, [navigate]);

    // Handlers
    const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

    useEffect(() => {
        try { localStorage.setItem('theme', theme); } catch { }
        const html = document.documentElement;
        if (theme === 'dark') html.classList.add('dark'); else html.classList.remove('dark');
    }, [theme]);

    useEffect(() => {
        document.title = `Plantlix ${appVersion}`;
    }, [appVersion]);

    // Data Fetching
    const loadData = useCallback(async () => {
        setAppLoading(true);
        try {
            // Fetch Plants (Public)
            const plantsRes = await fetch(`${API_URL}/api/plants/`);
            if (plantsRes.ok) {
                const plantsData = await plantsRes.json();
                setPlants(plantsData);
            }

            // Fetch Categories (Public)
            const catsRes = await fetch(`${API_URL}/api/categories/`);
            if (catsRes.ok) {
                const catsData = await catsRes.json();
                setCategories(catsData);
            }

            // Fetch Sponsors (Public)
            const sponsorsRes = await fetch(`${API_URL}/api/sponsors/`);
            if (sponsorsRes.ok) {
                setSponsors(await sponsorsRes.json());
            }

            // Fetch App Version (Public)
            const versionRes = await fetch(`${API_URL}/api/app-version/`);
            if (versionRes.ok) {
                const versionData = await versionRes.json();
                setAppVersion(versionData.version);
            }

            // Authenticated Data
            if (authToken && user) {
                // User Plant Data
                const updRes = await fetchWithAuth('/api/user-plant-data/');
                if (updRes.ok) {
                    const updData = await updRes.json();
                    setUserPlantData(updData);
                }

                // User Profile (Main stats)
                const profileRes = await fetchWithAuth('/api/profile/');
                if (profileRes.ok) {
                    const profileData = await profileRes.json();
                    setUserProfile(profileData);
                }

                // Achievements
                const achievementsRes = await fetchWithAuth('/api/achievements/');
                if (achievementsRes.ok) {
                    setAchievements(await achievementsRes.json());
                }

                // Daily Stats
                const statsRes = await fetchWithAuth('/api/stats/daily/');
                if (statsRes.ok) {
                    setDailyStats(await statsRes.json());
                }

                // Daily Challenge
                const challengeRes = await fetchWithAuth('/api/challenge/daily/');
                if (challengeRes.ok) {
                    setDailyChallenge(await challengeRes.json());
                }

                // Messages
                const msgsRes = await fetchWithAuth('/api/messages/');
                if (msgsRes.ok) {
                    setMessages(await msgsRes.json());
                }
            } else {
                // Reset user data if logged out
                setUserPlantData({});
                setUserProfile(null);
                setAchievements(null);
                setDailyStats(null);
                setDailyChallenge(null);
                setMessages({ received: [], sent: [], unread_count: 0 });
            }

        } catch (error) {
            console.error("Error loading data:", error);
        } finally {
            setAppLoading(false);
        }
    }, [authToken, user, fetchWithAuth]);

    useEffect(() => {
        if (!authLoading) {
            loadData();
        }
    }, [authLoading, authToken, user]); // Reload if auth state changes

    const trackInteraction = useCallback(async (type, data = {}) => {
        if (!authToken) return;
        try {
            await fetchWithAuth('/api/interactions/track/', {
                method: 'POST',
                body: JSON.stringify({ interaction_type: type, metadata: data })
            });
        } catch (e) {
            console.error("Tracking error:", e);
        }
    }, [authToken, fetchWithAuth]);

    const handleUpdateUserPlantData = (plantId, newData) => {
        setUserPlantData(prev => ({
            ...prev,
            [plantId]: { ...(prev[plantId] || {}), ...newData }
        }));
    };

    const reviewPlant = async (plant, knewIt, confidence = null, timeTaken = 0) => {
        if (!authToken) return;

        try {
            // Use confidence if provided, otherwise fallback to knew_it
            const payload = confidence
                ? { confidence, time_taken: timeTaken }
                : { knew_it: knewIt, time_taken: timeTaken };

            const res = await fetchWithAuth(`/api/plants/${plant.id}/review/`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const updatedData = await res.json();
                handleUpdateUserPlantData(plant.id, updatedData);
            }
        } catch (e) {
            console.error("Review error:", e);
        }
    };

    // Derived State
    const dueCount = plants.filter(p => {
        const d = userPlantData[p.id];
        if (!d || !d.next_review_at) return true;
        return Date.now() >= new Date(d.next_review_at).getTime();
    }).length;

    // Helpers
    const [selectedPlantList, setSelectedPlantList] = useState(() => {
        try {
            return localStorage.getItem('selectedPlantList') || 'GALABAUER';
        } catch {
            return 'GALABAUER';
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('selectedPlantList', selectedPlantList);
        } catch (e) {
            console.error("Failed to save list selection:", e);
        }

        // Trigger subtle loading state for list transition
        setListLoading(true);
        const timer = setTimeout(() => setListLoading(false), 800);
        return () => clearTimeout(timer);
    }, [selectedPlantList]);

    const [listLoading, setListLoading] = useState(false);

    const listStats = React.useMemo(() => {
        const stats = {
            'GALABAUER': 0,
            'FRIEDHOFSGAERTNER': 0,
            'ZIERPFLANZENGAERTNER': 0
        };
        plants.forEach(p => {
            if (p.plant_lists) {
                p.plant_lists.forEach(list => {
                    if (stats.hasOwnProperty(list)) {
                        stats[list]++;
                    }
                });
            }
        });
        return stats;
    }, [plants]);

    if (authLoading || appLoading) {
        return <LoadingSpinner />;
    }

    const layoutProps = {
        user,
        userProfile,
        logout,
        theme,
        toggleTheme,
        dueCount: user?.is_guest ? 0 : dueCount,
        achievementsBadge: achievements?.achievements?.filter(a => !a.seen)?.length || null, // Simplified
        unreadBadge: messages.unread_count > 0 ? messages.unread_count : null,
        plants,
        categories,
        userPlantData,
        appVersion
    };

    // Common props for pages
    const pageProps = {
        plants,
        categories,
        userPlantData,
        user,
        userProfile,
        fetchWithAuth,
        trackInteraction,
        achievements,
        dailyStats,
        dailyChallenge,
        messages,
        theme,
        onReview: reviewPlant,
        onPlantClick: setSelectedPlant,
        onUpdateUserPlantData: handleUpdateUserPlantData,
        selectedPlantList,
        setSelectedPlantList,
        listStats, // Pass down for dropdown UI
        listLoading,
        logout,
        appVersion
    };

    return (
        <>
            <Routes>
                {/* Public/Auth Routes */}
                <Route path="/auth" element={(!user || user.is_guest) ? <AuthPage /> : <Navigate to="/" />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />
                <Route path="/join/:code" element={<JoinGroup />} />

                {/* Layout Routes */}
                <Route element={<Layout {...layoutProps} />}>
                    <Route path="/" element={user ? <Home {...pageProps} sponsors={sponsors} handlePlantClick={setSelectedPlant} /> : <Navigate to="/auth" />} />
                    <Route path="/dashboard" element={user ? <Home {...pageProps} sponsors={sponsors} handlePlantClick={setSelectedPlant} /> : <Navigate to="/auth" />} />

                    <Route path="/quiz" element={<Quiz {...pageProps} />} />
                    <Route path="/career" element={<Career />} />
                    <Route path="/bestiary" element={<Bestiary {...pageProps} onPlantClick={setSelectedPlant} comparePlants={comparePlants} onCompareClick={setComparePlants} />} />
                    <Route path="/glossary" element={<Glossary />} />
                    <Route path="/scan" element={<PlantScanner />} />
                    <Route path="/support" element={<Support {...pageProps} />} />

                    {/* Protected Routes */}
                    <Route path="/profile" element={user && !user.is_guest ? <Profile {...pageProps} /> : <Navigate to="/" />} />
                    <Route path="/profile/:profileSlug" element={<PublicProfile />} />
                    <Route path="/badges" element={user && !user.is_guest ? <Badges {...pageProps} /> : <GuestBlockedView title="Badges" description="Melde dich an, um Badges zu sammeln!" icon="🏆" benefits={["Level aufsteigen", "Achievements sammeln"]} />} />
                    <Route path="/duel" element={user && !user.is_guest ? <Duel {...pageProps} /> : <GuestBlockedView title="Duell" description="Melde dich an, um Freunde herauszufordern!" icon="⚔️" benefits={["Gegen Freunde spielen", "Rangliste erklimmen"]} />} />

                    {/* Admin Only */}
                    <Route path="/admin-panel" element={user && user.is_staff ? <AdminPanel /> : <Navigate to="/" />} />
                    <Route path="/admin/enrichment" element={user && user.is_staff ? <PlantEnrichmentDashboard {...pageProps} /> : <Navigate to="/" />} />

                    {/* Coach / Ausbilder */}
                    <Route path="/coach" element={user && !user.is_guest ? <CoachDashboard /> : <Navigate to="/" />} />
                    <Route path="/coach/azubi/:id" element={user && !user.is_guest ? <CoachAzubiDetail /> : <Navigate to="/" />} />
                    <Route path="/coach/berichte" element={user && !user.is_guest ? <CoachReportInbox /> : <Navigate to="/" />} />
                    <Route path="/coach/quizze" element={user && !user.is_guest ? <QuizBuilder /> : <Navigate to="/" />} />
                    <Route path="/coach/gruppen" element={user && !user.is_guest ? <CoachGroups /> : <Navigate to="/" />} />

                    {/* Sniggle (Messages) */}
                    <Route path="/sniggle" element={user && !user.is_guest ? <Sniggle /> : <Navigate to="/" />} />

                    {/* Friends */}
                    <Route path="/friends" element={user && !user.is_guest ? <Friends /> : <Navigate to="/" />} />

                    {/* Apprentice Journal */}
                    <Route path="/apprentice-journal" element={user && !user.is_guest ? <ApprenticeJournal /> : <Navigate to="/" />} />
                    <Route path="/apprentice/week/:id" element={user && !user.is_guest ? <ApprenticeWeekDetail /> : <Navigate to="/" />} />
                    <Route path="/apprentice/pflanze-der-woche" element={user && !user.is_guest ? <PflanzeDerWocheList /> : <Navigate to="/" />} />
                    <Route path="/apprentice/pflanze-der-woche/:id" element={user && !user.is_guest ? <PflanzeDerWocheEditor {...pageProps} /> : <Navigate to="/" />} />
                    <Route path="/apprentice/herbarium" element={user && !user.is_guest ? <HerbariumManager {...pageProps} /> : <Navigate to="/" />} />
                    <Route path="/apprentice/herbarium/:id" element={user && !user.is_guest ? <HerbariumEditor {...pageProps} /> : <Navigate to="/" />} />

                    {/* Legal Routes */}
                    <Route path="/impressum" element={<Impressum />} />
                    <Route path="/datenschutz" element={<Datenschutz />} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/weather" element={<WeatherDetail />} />
                    <Route path="/weather/day/:dayIndex" element={<WeatherDayDetail />} />

                    {/* Admin Billing */}
                    <Route path="/admin/billing" element={user && user.is_staff ? <BillingAdmin /> : <Navigate to="/" />} />

                    {/* Party Game */}
                    <Route path="/party" element={<PartyHome {...pageProps} />} />


                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" />} />
                </Route>
            </Routes>

            {
                selectedPlant && (
                    <PlantDetailModal
                        plant={selectedPlant}
                        userPlantData={userPlantData}
                        categories={categories}
                        onClose={() => setSelectedPlant(null)}
                        onUpdateUserPlantData={handleUpdateUserPlantData}
                        appVersion={appVersion}
                    />
                )
            }
        </>
    );
};

const App = () => {
    const paypalOptions = {
        "client-id": "test", // User should replace with their Client ID
        "currency": "EUR",
        "intent": "capture",
        "components": "buttons",
        "vault": true // Needed for subscriptions
    };

    return (
        <Router>
            <AuthProvider>
                <ToastProvider>
                    <PayPalScriptProvider options={paypalOptions}>
                        <AppRoutes />
                    </PayPalScriptProvider>
                </ToastProvider>
            </AuthProvider>
        </Router>
    );
};

export default App;
